<template>
  <b-modal 
    ref="modal" 
    size="lg" 
    @hidden="reset" 
    centered 
    no-close-on-backdrop
    :hide-footer="true"
  >
    <template #modal-header="{ close }">
      <div class="d-flex justify-content-between align-items-center w-100">
        <h5 class="font-weight-bolder m-0">Add Repayment</h5>
        <b-button
          size="sm"
          variant="none"
          class="btn-icon btn-circle btn-hover-dark"
          title="Close"
          @click="close"
          v-b-tooltip.hover
        >
          <i class="material-icons icon-xl">close</i>
        </b-button>
      </div>
    </template>

    <template #default>
      <!-- Loading State -->
      <div v-if="loadingRepayments" class="loading-container">
        <div class="spinner"></div>
        <span>Loading repayments...</span>
      </div>

      <!-- No Data Message -->
      <div v-else-if="!loadingRepayments && !hasExistingRepayments" class="no-data-container">
        <div class="no-data-message">
          <i class="material-icons">info</i>
          <h6>No Existing Repayments Found</h6>
          <p>There are no existing repayments available for this business. Please create a finance request first before adding repayments.</p>
        </div>
      </div>

      <!-- Content -->
      <div v-else class="add-repayment-content">
        <!-- Form Section -->
        <div class="form-section">
                <div class="form-group">
        <label class="form-label">Existing Repayment <span class="text-danger">*</span></label>
        <select 
          v-model="formData.finance_request_id" 
          class="form-control"
          :class="{ 'is-invalid': validationErrors.finance_request_id }"
          :disabled="saving"
          @change="onRepaymentChange"
          required
        >
          <option value="">Select an existing repayment</option>
          <option 
            v-for="repayment in repayments" 
            :key="repayment.id"
            :value="repayment.id"
          >
            {{ repayment.invoice_reference }} - {{ formatCurrency(repayment.amount_due || repayment.repayment_amount, currency) }}
          </option>
        </select>
        <div v-if="validationErrors.finance_request_id" class="invalid-feedback d-block">
          {{ validationErrors.finance_request_id }}
        </div>
        <small v-if="selectedRepayment" class="form-text text-muted">
          Due Date: {{ formatDate(selectedRepayment.due_date) }} | Status: {{ formatStatus(selectedRepayment.status || selectedRepayment.due_status) }}
        </small>
      </div>

          <div class="form-group">
            <label class="form-label">Repaid Amount <span class="text-danger">*</span></label>
            <div class="input-group">
              <div class="input-group-prepend">
                <span class="input-group-text">{{ getCurrencySymbol(currency) }}</span>
              </div>
              <input 
                type="number" 
                v-model="formData.repaid_amount"
                class="form-control"
                min="0"
                step="0.01"
                :disabled="saving"
                placeholder="0.00"
                required
              />
            </div>
            <div v-if="validationErrors.repaid_amount" class="invalid-feedback d-block">
              {{ validationErrors.repaid_amount }}
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">Repaid Date <span class="text-danger">*</span></label>
            <div class="date-picker-wrapper">
              <v-date-picker 
                v-model="repaidDate"
                :disabled="saving"
                :max-date="new Date()"
                :select-attribute="datePickerAttribute"
                :input-debounce="300"
                is-required
              >
                <template v-slot="{ inputValue, inputEvents }">
                  <div class="input-group">
                    <input
                      class="form-control"
                      :class="{ 'is-invalid': validationErrors.repaid_at }"
                      :value="inputValue"
                      :disabled="saving"
                      placeholder="Select repaid date"
                      readonly
                      v-on="inputEvents"
                      required
                    />
                    <div class="input-group-append">
                      <span class="input-group-text">
                        <i class="las la-calendar"></i>
                      </span>
                    </div>
                  </div>
                </template>
              </v-date-picker>
            </div>
            <div v-if="validationErrors.repaid_at" class="invalid-feedback d-block">
              {{ validationErrors.repaid_at }}
            </div>
            <small class="form-text text-muted">
              Select the date when the repayment was made
            </small>
          </div>
        </div>

        <!-- Validation Error -->
        <div v-if="error" class="alert alert-danger">
          <i class="material-icons">error</i>
          {{ error }}
        </div>

        <!-- Action Buttons -->
        <div class="modal-footer-custom">
          <button 
            type="button" 
            class="btn btn-secondary" 
            @click="$refs.modal.hide()"
            :disabled="saving"
          >
            Cancel
          </button>
          <button 
            type="button" 
            class="btn btn-primary" 
            @click="saveRepayment"
            :disabled="!isValid || saving || !hasExistingRepayments"
            :title="!hasExistingRepayments ? 'No existing repayments available' : ''"
          >
            <span v-if="saving" class="spinner-border spinner-border-sm mr-2"></span>
            {{ saving ? 'Saving...' : 'Add Repayment' }}
          </button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import FinancingService from '@/services/financing.service';

export default {
  name: 'AddRepaymentModal',
  props: {
    businessId: {
      type: String,
      required: true
    },
    currency: {
      type: String,
      default: 'USD'
    }
  },
  data() {
    return {
      loadingRepayments: false,
      saving: false,
      error: null,
      repayments: [],
      formData: {
        finance_request_id: '',
        repaid_amount: '',
        repaid_at: ''
      },
      validationErrors: {}
    };
  },
  computed: {
    selectedRepayment() {
      if (!this.formData.finance_request_id) return null;
      return this.repayments.find(rep => rep.id === this.formData.finance_request_id);
    },
    isValid() {
      return this.formData.finance_request_id &&
             this.formData.repaid_amount && 
             parseFloat(this.formData.repaid_amount) > 0 &&
             this.formData.repaid_at &&
             Object.keys(this.validationErrors).length === 0 &&
             this.repayments.length > 0;
    },
    
    hasExistingRepayments() {
      return this.repayments && this.repayments.length > 0;
    },

    repaidDate: {
      get() {
        if (!this.formData.repaid_at) return null;
        return new Date(this.formData.repaid_at);
      },
      set(value) {
        if (value) {
          // Format date as YYYY-MM-DD for API
          const year = value.getFullYear();
          const month = String(value.getMonth() + 1).padStart(2, '0');
          const day = String(value.getDate()).padStart(2, '0');
          this.formData.repaid_at = `${year}-${month}-${day}`;
        } else {
          this.formData.repaid_at = '';
        }
      }
    },

    datePickerAttribute() {
      return {
        key: 'today',
        highlight: {
          color: 'blue',
          fillMode: 'light'
        },
        dates: new Date()
      };
    }
  },
  methods: {
    async open() {
      this.reset();
      this.$refs.modal.show();
      await this.loadRepayments();
    },
    
    async loadRepayments() {
      try {
        this.loadingRepayments = true;
        this.error = null;
        
        const response = await FinancingService.getRepayments(this.businessId);
        
        this.repayments = response.data || [];
        
      } catch (error) {
        console.error('Error loading repayments:', error);
        this.error = 'Failed to load repayments. You can still add a repayment without selecting a specific request.';
        this.repayments = [];
      } finally {
        this.loadingRepayments = false;
      }
    },
    
    onRepaymentChange() {
      // Clear any previous validation errors when selection changes
      this.validationErrors = {};
      
      // Prefill the repaid amount if a repayment is selected
      if (this.selectedRepayment) {
        const amount = this.selectedRepayment.amount_due || this.selectedRepayment.repayment_amount || 0;
        this.formData.repaid_amount = amount.toString();
      } else {
        // Clear the amount if no repayment is selected
        this.formData.repaid_amount = '';
      }
    },
    
    validateForm() {
      this.validationErrors = {};
      
      if (!this.formData.finance_request_id) {
        this.validationErrors.finance_request_id = 'Please select an existing repayment';
      }
      
      if (!this.formData.repaid_amount) {
        this.validationErrors.repaid_amount = 'Repaid amount is required';
      } else if (parseFloat(this.formData.repaid_amount) <= 0) {
        this.validationErrors.repaid_amount = 'Repaid amount must be greater than 0';
      }
      
      if (!this.formData.repaid_at) {
        this.validationErrors.repaid_at = 'Repaid date is required';
      } else {
        const repaidDate = new Date(this.formData.repaid_at);
        const today = new Date();
        today.setHours(23, 59, 59, 999); // Set to end of today
        
        if (repaidDate > today) {
          this.validationErrors.repaid_at = 'Repaid date cannot be in the future';
        }
      }
      
      return Object.keys(this.validationErrors).length === 0;
    },
    
    async saveRepayment() {
      if (!this.validateForm()) {
        return;
      }
      
      try {
        this.saving = true;
        this.error = null;
        
        const repaymentData = {
          business_id: this.businessId,
          repaid_amount: parseFloat(this.formData.repaid_amount),
          repaid_at: this.formData.repaid_at
        };
        
        // Only include finance_request_id if it's selected
        if (this.formData.finance_request_id) {
          repaymentData.finance_request_id = this.formData.finance_request_id;
        }
        
        await FinancingService.createRepayment(repaymentData);
        
        this.$showAlert('Repayment added successfully', 'success');
        this.$emit('repayment-added');
        this.$refs.modal.hide();
        
      } catch (error) {
        console.error('Error saving repayment:', error);
        this.error = error.response?.data?.message || 'Failed to add repayment. Please try again.';
      } finally {
        this.saving = false;
      }
    },
    
    formatCurrency(amount, currency) {
      if (amount === undefined || amount === null || isNaN(amount)) {
        return Number(0).toLocaleString(undefined, { 
          style: 'currency', 
          currency: currency || 'USD', 
          minimumFractionDigits: 2 
        });
      }
      return Number(amount).toLocaleString(undefined, { 
        style: 'currency', 
        currency: currency || 'USD', 
        minimumFractionDigits: 2 
      });
    },
    
    getCurrencySymbol(currency) {
      const symbols = {
        'USD': '$',
        'EUR': '€',
        'GBP': '£',
        'AED': 'د.إ',
        'SAR': 'ر.س'
      };
      return symbols[currency] || currency;
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },

    formatStatus(status) {
      if (status === 'paid') return 'Paid';
      if (status === 'overdue') return 'Overdue';
      if (status === 'pending') return 'Pending';
      return status;
    },
    
    reset() {
      this.loadingRepayments = false;
      this.saving = false;
      this.error = null;
      this.repayments = [];
      this.formData = {
        finance_request_id: '',
        repaid_amount: '',
        repaid_at: ''
      };
      this.validationErrors = {};
    }
  }
};
</script>

<style scoped lang="scss">
.add-repayment-content {
  .form-section {
    margin-bottom: 1.5rem;
    
    .form-group {
      margin-bottom: 1.25rem;
      
      .form-label {
        font-weight: 600;
        color: #1e293b;
        margin-bottom: 0.5rem;
        display: block;
        font-size: 0.875rem;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      
      .form-control {
        border: 1px solid #E5E5E5;
        border-radius: 0.375rem;
        padding: 0.75rem 1rem;
        font-size: 0.875rem;
        transition: all 0.3s ease;
        background-color: white;
        color: #212529;
        
        &:focus {
          border-color: #1e293b;
          box-shadow: 0 0 0 0.2rem rgba(30, 41, 59, 0.1);
          outline: none;
        }
        
        &:disabled {
          background-color: #f8f9fa;
          opacity: 0.6;
        }
        
        &::placeholder {
          color: #6c757d;
        }
        
        &.is-invalid {
          border-color: #dc3545;
          
          &:focus {
            border-color: #dc3545;
            box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
          }
        }
      }
      
      select.form-control {
        appearance: none;
        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 6 7 7 7-7'/%3e%3c/svg%3e");
        background-repeat: no-repeat;
        background-position: right 1rem center;
        background-size: 16px 12px;
        padding-right: 3rem;
        cursor: pointer;
        
        option {
          padding: 0.5rem;
          color: #212529;
          background-color: white;
        }
      }
      
      .input-group-text {
        background-color: #f8f9fa;
        border: 1px solid #E5E5E5;
        color: #6c757d;
        font-weight: 600;
        transition: all 0.3s ease;
        
        i {
          font-size: 1.1rem;
        }
      }
      
      .date-picker-wrapper {
        .input-group {
          .form-control {
            cursor: pointer;
            
            &:focus {
              border-color: #1e293b;
              box-shadow: 0 0 0 0.2rem rgba(30, 41, 59, 0.1);
            }
          }
          
          .input-group-append .input-group-text {
            border-left: 0;
            cursor: pointer;
            
            &:hover {
              background-color: #e9ecef;
              color: #1e293b;
            }
          }
        }
      }
      
      .form-text {
        font-size: 0.8rem;
        color: #6c757d;
        margin-top: 0.25rem;
      }
    }
  }
  
  // Date picker styling
  .vc-container {
    border: 1px solid #E5E5E5;
    border-radius: 0.375rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    
    .vc-header {
      padding: 1rem;
      background-color: #f8f9fa;
      border-bottom: 1px solid #E5E5E5;
      
      .vc-title {
        color: #1e293b;
        font-weight: 600;
      }
    }
    
    .vc-weeks {
      padding: 0.5rem;
    }
    
    .vc-day {
      &.is-today .vc-day-content {
        background-color: #1e293b !important;
        color: white !important;
        border-color: #1e293b !important;
      }
      
      &:hover .vc-day-content {
        background-color: #f1f5f9;
        border-color: #1e293b;
      }
    }
  }
  
  .loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 2rem;
    color: #6c757d;
    
    .spinner {
      width: 24px;
      height: 24px;
      border: 2px solid #f3f4f6;
      border-top: 2px solid #1e293b;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }
    
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  }
  
  .no-data-container {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    
    .no-data-message {
      text-align: center;
      color: #6c757d;
      max-width: 400px;
      
      i {
        font-size: 3rem;
        color: #ffc107;
        margin-bottom: 1rem;
        display: block;
      }
      
      h6 {
        font-size: 1.1rem;
        font-weight: 600;
        color: #1e293b;
        margin-bottom: 0.75rem;
      }
      
      p {
        font-size: 0.9rem;
        line-height: 1.5;
        margin-bottom: 0;
      }
    }
  }
  
  .modal-footer-custom {
    display: flex;
    gap: 0.75rem;
    justify-content: flex-end;
    padding-top: 1.5rem;
    border-top: 1px solid #E5E5E5;
    margin-top: 1.5rem;
    
    .btn {
      padding: 0.75rem 1.5rem;
      font-weight: 600;
      border-radius: 0.375rem;
      transition: all 0.3s ease;
      font-size: 0.875rem;
      border-width: 1px;
      border-style: solid;
      
      &.btn-secondary {
        background-color: #6c757d;
        border-color: #6c757d;
        color: white;
        
        &:hover:not(:disabled) {
          background-color: #5a6268;
          border-color: #545b62;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
        }
      }
      
      &.btn-primary {
        background: linear-gradient(135deg, #1e293b 0%, #495057 100%);
        border-color: #1e293b;
        color: white;
        
        &:hover:not(:disabled) {
          background: linear-gradient(135deg, #000000 0%, #1e293b 100%);
          border-color: #000000;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(30, 41, 59, 0.3);
        }
        
        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      }
      
      .spinner-border {
        &.spinner-border-sm {
          width: 1rem;
          height: 1rem;
          margin-right: 0.5rem;
        }
      }
    }
  }
  
  .alert {
    margin-bottom: 1rem;
    border-radius: 0.5rem;
    padding: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    &.alert-danger {
      background-color: #fef2f2;
      border-color: #fecaca;
      color: #991b1b;
      
      i {
        color: #dc2626;
      }
    }
    
    i {
      vertical-align: text-bottom;
      font-size: 1.1rem;
    }
  }
  
  .invalid-feedback {
    font-size: 0.8rem;
    margin-top: 0.25rem;
    color: #dc3545;
    font-weight: 500;
  }
}

// Responsive design
@media (max-width: 768px) {
  .add-repayment-content {
    .form-section {
      .form-group {
        .form-control {
          padding: 0.75rem;
          font-size: 1rem;
        }
      }
    }
    
    .modal-footer-custom {
      flex-direction: column;
      gap: 0.5rem;
      
      .btn {
        width: 100%;
        justify-content: center;
      }
    }
  }
}
</style> 